/**
 * Live view of the runner's browser, and supervisor takeover.
 *
 * What the panel can be showing:
 *
 *   loading            control state not known yet (skeleton)
 *   permission denied  the viewer's role may not watch (PHI on screen)
 *   not available      the job is not on a runner, or live view is not
 *                      set up in this environment
 *   ready              "Watch live" (opening a view is audited, so it
 *                      is a deliberate click, not automatic)
 *   connecting / live / frames withheld / runner not connected /
 *   retrying / ended   while a view session is open or just after
 *   taken over by <name>   another supervisor holds control
 *   taking control         asked for; the agent has not paused yet
 *   you are in control     the runner has confirmed the agent is paused
 *
 * "You are in control" is never shown on the strength of the request
 * alone. The agent finishes the step it is in and pauses at its next
 * gate; only when the runner reports that (its frame mode, or the job
 * status) does the panel say so and start forwarding input.
 *
 * Keyboard and mouse go to the remote page ONLY while this supervisor
 * holds control AND the remote screen has focus. Shift+Escape leaves
 * the remote screen, so the keyboard is never trapped in it.
 *
 * Nothing here stores a frame or an input event: frames are painted to
 * a canvas and dropped (`useLiveView`), input is sent and dropped
 * (`InputBatcher`).
 */
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Eye, EyeOff, Hand, Keyboard, MonitorOff } from "lucide-react";

import { ApiError } from "@/lib/apiClient";

import {
  sendLiveInput,
  useHandBack,
  useLiveState,
  useTakeControl,
  type LiveInputEvent,
  type LiveState,
} from "./liveApi";
import {
  InputBatcher,
  isReleaseKey,
  keyToEvent,
  mouseButton,
  textToEvents,
  toViewportPoint,
  wheelEvent,
} from "./liveInput";
import { DeniedNotice, ErrorNotice, Panel, Skeleton } from "./parts";
import { BTN, BTN_DANGER, BTN_PRIMARY, isStatus, LIVE_VIEW_ID } from "./ui";
import { useLiveView, type EndReason, type LivePhase } from "./useLiveView";

const VIEW_ROLES = "supervisor or entity admin";

function fmtRemaining(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function takeControlError(error: unknown): string {
  if (isStatus(error, 409)) {
    const code = error instanceof ApiError ? error.message : "";
    if (code === "takeover_held") return "Another supervisor has control of this job.";
    if (code === "takeover_not_supported") {
      return "This is a smoke run with no agent to pause, so it cannot be taken over.";
    }
    if (code === "approval_pending") {
      return "This job is waiting for an approval. Approve or deny it before taking control.";
    }
    return "This job is no longer on a runner.";
  }
  if (isStatus(error, 403)) return `Taking control needs the ${VIEW_ROLES} role.`;
  if (isStatus(error, 404)) return "The view session has ended. Watch live again, then take control.";
  if (isStatus(error, 429)) return "Too many requests. Wait a minute, then try again.";
  if (isStatus(error, 503)) return "Live view is not available right now, so control was not taken.";
  return "Control was not taken. Try again.";
}

function handBackError(error: unknown): string {
  if (isStatus(error, 409)) return "You no longer hold control of this job. It may have timed out.";
  if (isStatus(error, 403)) return `Handing back needs the ${VIEW_ROLES} role.`;
  if (isStatus(error, 429)) return "Too many requests. Wait a minute, then try again.";
  return "Control was not handed back. Try again.";
}

/**
 * What to do, by the runner's reason for asking. A CAPTCHA is the one a
 * person most often gets wrong: they only need to tick the box, and
 * Flow types the login and the emailed code itself after the hand-back.
 */
const HELP_GUIDANCE: Record<string, { title: string; text: string }> = {
  captcha_encountered: {
    title: "Paused for a CAPTCHA.",
    text:
      "Take control, tick the “I’m not a robot” box (and finish any picture check), " +
      "then hand back. Do not type the username, password or emailed code: Flow signs in " +
      "by itself after you hand back.",
  },
  captcha_tick_then_submit: {
    title: "Flow has filled in the login. Tick the CAPTCHA box, then hand back.",
    text:
      "Tick the box and hand back within a minute; the tick expires after two minutes. " +
      "Flow presses Sign in the moment you hand back. Do not type anything.",
  },
  captcha_tick_expired: {
    title: "The CAPTCHA tick expired before Flow could sign in.",
    text:
      "Tick the box again and hand back within a minute; the tick expires after two " +
      "minutes. If it still shows a tick, wait until it says the verification expired, " +
      "then tick it.",
  },
  captcha_tick_after_rejected_submit: {
    title: "The portal did not accept Flow's sign-in attempt, which used up the tick.",
    text:
      "A required field was empty when Flow pressed Sign in. Flow has filled the login " +
      "again. Tick the box once more and hand back within a minute; the tick expires " +
      "after two minutes. Do not type anything.",
  },
  sign_in_username_not_found: {
    title: "Flow could not find the username field on this page.",
    text:
      "Take control and sign in yourself (username, password, the CAPTCHA, then Sign in), " +
      "then hand back. Flow continues with the emailed code.",
  },
  sign_in_field_empty: {
    title: "Flow could not fill in the login: a field stayed empty.",
    text:
      "Flow did not press Sign in. Take control, check the username and password fields, " +
      "complete the sign-in, then hand back.",
  },
  sign_in_not_accepted: {
    title: "The portal refused the sign-in with the login filled in.",
    text:
      "Flow will not try again on its own (repeated attempts can lock the login). Take " +
      "control and check the message on the page; sign in yourself or end the job.",
  },
  sign_in_submit_limit: {
    title: "Flow has pressed Sign in the most times it is allowed for one job.",
    text:
      "Take control and finish the sign-in yourself, or end the job. Repeated attempts can " +
      "lock the login.",
  },
  mfa_code_timeout: {
    title: "The emailed sign-in code did not arrive in time.",
    text: "Take control to finish or abandon the sign-in, then hand back or end the job.",
  },
  sign_in_unverified: {
    title: "Flow could not confirm the portal is signed in.",
    text: "Take control, check the page and finish signing in if needed, then hand back.",
  },
};

function helpGuidance(reason: string | null | undefined) {
  return reason ? (HELP_GUIDANCE[reason] ?? null) : null;
}

const ENDED_TEXT: Record<EndReason, string> = {
  closed: "You stopped watching.",
  expired: "The view session ended. Sessions last a limited time.",
  job_finished: "The job is no longer available to watch.",
  not_running: "The job is not on a runner, so there is nothing to watch.",
  unavailable: "Live view is not available right now.",
};

function phaseLine(phase: LivePhase, frameAgeMs: number | null): string {
  switch (phase) {
    case "connecting":
      return "Connecting to the runner's browser…";
    case "live":
      return frameAgeMs !== null && frameAgeMs > 3000
        ? "Live. The page has not changed for a few seconds."
        : "Live";
    case "withheld":
      return "Screen hidden while a sign-in credential is visible on the page.";
    case "offline":
      return "The runner is not connected to live view. Waiting for it…";
    case "retrying":
      return "Live view was interrupted. Reconnecting…";
    default:
      return "";
  }
}

export function LiveViewPanel({ jobId, jobActive }: { jobId: string; jobActive: boolean }) {
  const ids = useId();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const stateQuery = useLiveState(jobId, jobActive);
  const view = useLiveView(jobId, canvasRef);
  const take = useTakeControl(jobId);
  const handBack = useHandBack(jobId);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [inputError, setInputError] = useState<unknown>(null);
  const [focused, setFocused] = useState(false);
  const handBackRef = useRef<HTMLButtonElement | null>(null);

  const state = stateQuery.data;
  const watching = view.sessionId !== null;
  const takeover = state?.takeover;
  // This tab's session holds the takeover (as far as the API knows)…
  const holdsControl = Boolean(
    watching && takeover?.live && takeover.byMe && takeover.sessionId === view.sessionId,
  );
  // …and the runner has confirmed the agent is paused.
  const runnerPaused = view.mode === "human" || state?.status === "paused_for_takeover";
  const inControl = holdsControl && runnerPaused;
  const takingControl = holdsControl && !runnerPaused;
  const controlledElsewhere = Boolean(takeover?.held && takeover.live && !holdsControl);
  const guidance = jobActive ? helpGuidance(state?.helpReason) : null;
  const viewportWidth = state?.viewport.width ?? 1440;
  const viewportHeight = state?.viewport.height ?? 900;

  // The job left its runner (finished, stopped): end the view.
  const stopView = view.stop;
  useEffect(() => {
    if (watching && state && !state.onRunner) stopView();
  }, [watching, state, stopView]);

  // ── input ──────────────────────────────────────────────────
  const batcher = useRef<InputBatcher | null>(null);
  const sessionId = view.sessionId;
  const refetchState = stateQuery.refetch;
  useEffect(() => {
    if (!inControl || !sessionId) return undefined;
    setInputError(null);
    const instance = new InputBatcher(
      (events: LiveInputEvent[]) => sendLiveInput(jobId, sessionId, events),
      (error) => {
        setInputError(error);
        void refetchState();
      },
    );
    batcher.current = instance;
    return () => {
      instance.stop();
      batcher.current = null;
    };
  }, [inControl, jobId, sessionId, refetchState]);

  const point = useCallback(
    (clientX: number, clientY: number) => {
      const canvas = canvasRef.current;
      return canvas
        ? toViewportPoint(clientX, clientY, canvas.getBoundingClientRect(), {
            width: viewportWidth,
            height: viewportHeight,
          })
        : null;
    },
    [viewportWidth, viewportHeight],
  );

  // Wheel needs a non-passive listener to stop the page behind from scrolling.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !inControl) return undefined;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const at = point(event.clientX, event.clientY);
      if (at) batcher.current?.push(wheelEvent(at, event.deltaX, event.deltaY));
    };
    canvas.addEventListener("wheel", onWheel, { passive: false });
    return () => canvas.removeEventListener("wheel", onWheel);
  }, [inControl, point]);

  const onMouse = (type: "mouse_move" | "mouse_down" | "mouse_up") =>
    (event: React.MouseEvent<HTMLCanvasElement>) => {
      if (!inControl) return;
      const at = point(event.clientX, event.clientY);
      if (!at) return;
      if (type === "mouse_move") {
        batcher.current?.push({ type, ...at });
        return;
      }
      const button = mouseButton(event.button);
      if (!button) return;
      event.preventDefault();
      if (type === "mouse_down") canvasRef.current?.focus();
      batcher.current?.push({ type, ...at, button });
    };

  const onKeyDown = (event: React.KeyboardEvent<HTMLCanvasElement>) => {
    if (!inControl) return;
    if (isReleaseKey(event)) {
      // The way out for keyboard users: back to the page's own controls.
      event.preventDefault();
      handBackRef.current?.focus();
      return;
    }
    const mapped = keyToEvent(event);
    if (!mapped) return; // not forwarded: the browser handles it as usual
    event.preventDefault();
    batcher.current?.push(mapped);
  };

  const onPaste = (event: React.ClipboardEvent<HTMLCanvasElement>) => {
    if (!inControl) return;
    event.preventDefault();
    for (const item of textToEvents(event.clipboardData.getData("text"))) {
      batcher.current?.push(item);
    }
  };

  // ── render ─────────────────────────────────────────────────
  return (
    <Panel id={LIVE_VIEW_ID} title="Live view" aside={watching ? "Not recorded" : undefined}>
      <div className="space-y-3 text-sm">
        <Body
          ids={ids}
          jobActive={jobActive}
          stateQuery={stateQuery}
          state={state}
          view={view}
          watching={watching}
        />

        {guidance && (
          <div
            id={`${ids}-help`}
            role="note"
            aria-label="What to do"
            className="flex items-start gap-2 rounded-sm border border-status-takeover bg-status-takeover-bg px-3 py-2 text-ink-1"
          >
            <Hand size={14} className="mt-0.5 shrink-0 text-status-takeover" aria-hidden />
            <p>
              <strong className="font-medium">{guidance.title}</strong> {guidance.text}
            </p>
          </div>
        )}

        {/* The canvas stays mounted so the frame loop always has a target. */}
        <div hidden={!watching}>
          {inControl && (
            <div
              role="status"
              className="mb-2 flex flex-wrap items-center gap-2 rounded-sm border border-status-takeover bg-status-takeover-bg px-3 py-2 text-status-takeover"
            >
              <Hand size={14} aria-hidden />
              <strong className="font-medium">You are in control.</strong>
              <span>The agent is paused and will not act until you hand back.</span>
              {takeover && (
                <span className="ml-auto font-mono text-xs">
                  Ends in {fmtRemaining(takeover.remainingSec)} without input
                </span>
              )}
            </div>
          )}
          {takingControl && (
            <p
              role="status"
              className="mb-2 rounded-sm border border-paper-line-strong bg-paper-3 px-3 py-2 text-ink-1"
            >
              Taking control. Waiting for the agent to finish its current step and pause.
              Your input is not sent until it has.
            </p>
          )}
          {controlledElsewhere && takeover?.by && (
            <p
              role="status"
              className="mb-2 rounded-sm border border-paper-line-strong bg-paper-3 px-3 py-2 text-ink-1"
            >
              Taken over by {takeover.by.name}. The agent is paused. You are watching only.
            </p>
          )}

          <canvas
            ref={canvasRef}
            width={viewportWidth}
            height={viewportHeight}
            tabIndex={inControl ? 0 : -1}
            // `application` while in control: keys are handled here, not by
            // the reader's own shortcuts. A plain image otherwise.
            role={inControl ? "application" : "img"}
            aria-label={
              inControl
                ? "Remote browser. You are in control. Press Shift and Escape to leave."
                : "Remote browser, view only"
            }
            aria-describedby={`${ids}-line`}
            onMouseMove={onMouse("mouse_move")}
            onMouseDown={onMouse("mouse_down")}
            onMouseUp={onMouse("mouse_up")}
            onContextMenu={(event) => inControl && event.preventDefault()}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            className={[
              "block h-auto w-full rounded-sm border bg-paper-tint focus:outline-none",
              inControl
                ? "cursor-crosshair border-status-takeover focus-visible:ring-2 focus-visible:ring-status-takeover"
                : "border-paper-line",
              inControl && focused ? "ring-2 ring-status-takeover" : "",
            ].join(" ")}
          />

          <p id={`${ids}-line`} role="status" className="mt-1 text-xs text-ink-3">
            {phaseLine(view.phase, view.frameAgeMs)}
          </p>

          {inControl && (
            <p className="flex items-center gap-1 text-xs text-ink-2">
              <Keyboard size={12} aria-hidden />
              {focused
                ? "Your keyboard is going to the remote page. Press Shift+Escape to leave it."
                : "Click the remote screen to type into it. Keys go there only while it has focus."}
            </p>
          )}

          <div className="mt-3 flex flex-wrap items-start gap-2">
            {holdsControl ? (
              confirmEnd ? (
                <div
                  role="alertdialog"
                  aria-label="Confirm end job"
                  className="rounded-sm border border-status-failed/40 p-3"
                >
                  <p className="text-xs text-ink-1">
                    End this job here? The agent will not continue and the job cannot be
                    restarted.
                  </p>
                  <div className="mt-2 flex gap-2">
                    <button
                      type="button"
                      className={BTN_DANGER}
                      disabled={handBack.isPending}
                      onClick={() =>
                        handBack.mutate("end", { onSettled: () => setConfirmEnd(false) })
                      }
                    >
                      End job
                    </button>
                    <button
                      type="button"
                      className={BTN}
                      disabled={handBack.isPending}
                      onClick={() => setConfirmEnd(false)}
                    >
                      Keep control
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <button
                    ref={handBackRef}
                    type="button"
                    className={BTN_PRIMARY}
                    disabled={handBack.isPending}
                    aria-describedby={guidance ? `${ids}-help` : undefined}
                    onClick={() => handBack.mutate("continue")}
                  >
                    Hand back and continue
                  </button>
                  <button
                    type="button"
                    className={BTN_DANGER}
                    disabled={handBack.isPending}
                    onClick={() => setConfirmEnd(true)}
                  >
                    End job here
                  </button>
                </>
              )
            ) : (
              state?.canControl && (
                <div>
                  <button
                    type="button"
                    className={BTN_PRIMARY}
                    disabled={
                      take.isPending ||
                      controlledElsewhere ||
                      !view.sessionId ||
                      !state.takeoverSupported ||
                      state.status === "awaiting_approval"
                    }
                    aria-describedby={`${ids}-take`}
                    onClick={() => view.sessionId && take.mutate({ sessionId: view.sessionId })}
                  >
                    <Hand size={12} aria-hidden /> Take control
                  </button>
                  <p id={`${ids}-take`} className="mt-1 text-xs text-ink-3">
                    {!state.takeoverSupported
                      ? "Not available for a smoke run: it has no agent to pause. You can watch it and stop it."
                      : state.status === "awaiting_approval"
                      ? "Approve or deny the pending approval first."
                      : controlledElsewhere
                        ? "Another supervisor has control."
                        : "Pauses the agent. Use it to complete a step it must not do, such as a CAPTCHA."}
                  </p>
                </div>
              )
            )}
            {!holdsControl && (
              <button type="button" className={`${BTN} ml-auto`} onClick={view.stop}>
                <EyeOff size={12} aria-hidden /> Stop watching
              </button>
            )}
          </div>

          {take.isError && <ErrorNotice message={takeControlError(take.error)} error={take.error} />}
          {handBack.isError && (
            <ErrorNotice message={handBackError(handBack.error)} error={handBack.error} />
          )}
          {inControl && inputError !== null && (
            <ErrorNotice
              message={
                isStatus(inputError, 409)
                  ? "Your input was not sent: you no longer hold control."
                  : "Some input was not sent. Check the screen before continuing."
              }
              error={inputError}
            />
          )}
        </div>
      </div>
    </Panel>
  );
}

/** Everything the panel says when the canvas is not the main content. */
function Body({
  ids,
  jobActive,
  stateQuery,
  state,
  view,
  watching,
}: {
  ids: string;
  jobActive: boolean;
  stateQuery: ReturnType<typeof useLiveState>;
  state: LiveState | undefined;
  view: ReturnType<typeof useLiveView>;
  watching: boolean;
}) {
  if (watching) return null;

  if (!jobActive) {
    return (
      <Notice icon="off">
        <p className="text-ink-1">Ended.</p>
        <p className="mt-0.5 text-xs text-ink-3">
          The job has finished, so there is no browser to watch. Live view is not recorded.
        </p>
      </Notice>
    );
  }
  if (stateQuery.isPending) {
    return (
      <div role="status" aria-busy="true">
        <span className="sr-only">Loading live view</span>
        <Skeleton className="h-24" />
      </div>
    );
  }
  if (isStatus(stateQuery.error, 403) || view.phase === "denied" || (state && !state.canView)) {
    return <DeniedNotice what="the live view" roles={VIEW_ROLES} />;
  }
  if (!state) {
    return (
      <ErrorNotice
        message="Live view status could not be loaded."
        error={stateQuery.error}
        onRetry={() => stateQuery.refetch()}
      />
    );
  }
  if (!state.liveConfigured) {
    return (
      <Notice icon="off">
        <p className="text-ink-1">Not available for this job.</p>
        <p className="mt-0.5 text-xs text-ink-3">
          Live view is not set up in this environment. Stop run still works.
        </p>
      </Notice>
    );
  }
  if (!state.onRunner) {
    return (
      <Notice icon="off">
        <p className="text-ink-1">Not available for this job yet.</p>
        <p className="mt-0.5 text-xs text-ink-3">
          Live view starts once a runner has picked the job up.
        </p>
      </Notice>
    );
  }

  const controller = state.takeover.held && state.takeover.live ? state.takeover.by : null;
  return (
    <div>
      {view.phase === "ended" && view.endReason && (
        <p role="status" className="mb-2 text-xs text-ink-2">
          {ENDED_TEXT[view.endReason]}
        </p>
      )}
      {view.phase === "error" && (
        <div className="mb-2">
          <ErrorNotice
            message={
              view.endReason === "unavailable"
                ? "Live view is not available right now."
                : "Live view could not be started."
            }
            error={
              view.correlationId ? new ApiError("live_view", 500, view.correlationId) : null
            }
          />
        </div>
      )}
      {controller && (
        <p role="status" className="mb-2 text-ink-1">
          Taken over by {controller.name}. The agent is paused.
        </p>
      )}
      <button
        type="button"
        className={BTN_PRIMARY}
        aria-describedby={`${ids}-watch`}
        onClick={view.start}
        disabled={view.phase === "connecting"}
      >
        <Eye size={12} aria-hidden /> Watch live
      </button>
      <p id={`${ids}-watch`} className="mt-1 text-xs text-ink-3">
        Shows the runner's browser, which can include patient information. Opening and closing
        the view is logged with your name. The screen is not recorded.
      </p>
    </div>
  );
}

function Notice({ icon, children }: { icon: "off"; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2">
      {icon === "off" && <MonitorOff size={16} className="mt-0.5 shrink-0 text-ink-4" aria-hidden />}
      <div>{children}</div>
    </div>
  );
}
